import taskManagerApi from "@/features/shared/services/taskManagerApi";
const normalizeStatesList = (states) => {
    if (!Array.isArray(states)) {
        return [];
    }
    return states
        .filter((state) => typeof state === "object" && state !== null)
        .map((state) => {
        const normalizeList = (value) => {
            if (Array.isArray(value)) {
                return value.filter((item) => typeof item === "string");
            }
            if (typeof value === "string") {
                const items = value
                    .split(",")
                    .map((item) => item.trim())
                    .filter((item) => item.length > 0);
                return items.length > 0 ? items : null;
            }
            return null;
        };
        const toNumberOrNull = (value) => {
            if (typeof value === "number" && !Number.isNaN(value)) {
                return value;
            }
            if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
                return Number(value);
            }
            return null;
        };
        const safeId = toNumberOrNull(state.id) ?? 0;
        const processSequence = toNumberOrNull(state.process_sequence);
        const displaySequence = toNumberOrNull(state.display_sequence) ?? 0;
        return {
            id: safeId,
            code: String(state.code ?? ""),
            label: String(state.label ?? ""),
            color: typeof state.color === "string" ? state.color : null,
            icon_svg: typeof state.icon_svg === "string" ? state.icon_svg : null,
            process_sequence: processSequence,
            display_sequence: displaySequence,
            indic_display: Boolean(state.indic_display),
            indic_relance: Boolean(state.indic_relance),
            destination_states: normalizeList(state.destination_states),
            source_states: normalizeList(state.source_states),
        };
    });
};
const sanitizeRequestPayload = (payload) => Object.entries(payload).reduce((acc, [key, value]) => {
    if (value !== undefined) {
        acc[key] = value;
    }
    return acc;
}, {});
/**
 * Fetch all task states.
 */
export async function fetchTaskStates() {
    try {
        const response = await taskManagerApi.get("/state");
        const payload = response.data?.data ?? {};
        return {
            states: normalizeStatesList(payload.states),
            meta: payload.meta,
        };
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la récupération des états");
        }
    }
}
/**
 * Create (add) a new task state.
 */
export async function addState(newState) {
    try {
        if (!newState.code || !newState.label) {
            throw new Error("Les champs code et label sont obligatoires.");
        }
        const response = await taskManagerApi.post("/state", sanitizeRequestPayload(newState));
        return response.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la création de l'état");
        }
    }
}
/**
 * Update an existing task state.
 */
export async function updateState(updatedState) {
    try {
        const { id, ...payload } = updatedState;
        if (!id) {
            throw new Error("Identifiant de l'état manquant.");
        }
        const hasUpdatableField = [
            payload.code,
            payload.label,
            payload.sequence,
            payload.indic_display,
            payload.indic_relance,
            payload.color,
            payload.icon_svg,
            payload.destination_states,
            payload.source_states,
        ].some((value) => value !== undefined);
        if (!hasUpdatableField) {
            throw new Error("Au moins un champ doit être fourni pour la mise à jour.");
        }
        const response = await taskManagerApi.put(`/state/${id}`, sanitizeRequestPayload(payload));
        return response.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la modification de l'état");
        }
    }
}
/**
 * Delete a task state by ID.
 */
export async function deleteState(id) {
    try {
        const response = await taskManagerApi.delete(`/state/${id}`);
        return response.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la suppression de l'état");
        }
    }
}
/**
 * Switch the order of two states by ID.
 */
export async function switchStateOrder(idState, idStateToSwitchWith) {
    try {
        const response = await taskManagerApi.post(`/state/${idState}`, {
            switch_with: idStateToSwitchWith,
        });
        return response.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors du switch des états");
        }
    }
}
